import React, { useEffect, useMemo, useRef, useState } from "react";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  TextField,
  Typography,
} from "@mui/material";
import Flx from "../../../../components/layout/Flx";
import { useBorrowerPortalHook } from "../../../../hooks/useBorrowerPortalHook";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CACHED_DATA_REDUCER_TYPES } from "../../../../redux/reducers/cachedDataReducer";
import { isArray, isEmpty } from "lodash";
import DetailField from "../shared/DetailField";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { formatPhone } from "../../../../_src_shared/utils/functions/formatPhone";
import {
  CheckOutlined,
  DescriptionOutlined,
  EmailOutlined,
  InfoOutlined,
  OpenInNewOutlined,
  PersonAddOutlined,
  SearchOutlined,
  SendOutlined,
  WestOutlined,
} from "@mui/icons-material";
import Txt from "../../../../components/typography/Txt";
import Htag from "../../../../components/typography/Htag";
import { blue, green, grey } from "@mui/material/colors";
import GenericBlockEmailEditor from "../../../../components/Email/GenericBlockEmailEditor";
import RffEmailField from "../../../../components/finalForm/inputs/RffEmailField";
import RffTextField from "../../../../components/finalForm/inputs/RffTextField";
import RffGroup from "../../../../components/finalForm/shared/RffGroup";
import RffPhoneField from "../../../../components/finalForm/inputs/RffPhoneField";
import RffForm from "../../../../components/finalForm/RffForm";
import { useForm, useFormState } from "react-final-form";
import ResizeableSidebar from "../../../../components/sidebar/ResizeableSidebar";
import { CreateLoanFormQuestions } from "../../../LoanQuickIntakeFormScreen/LoanQuickIntakeFormScreen";
// const TEMP_DISABLED = true;
const TEMP_DISABLED = false;
const CACHED_DATA_KEY = CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS;

const STEP_TYPES = {
  FOUND_LIVE: "FOUND_LIVE",
  FOUND_STAGED: "FOUND_STAGED",
  CREATE: "CREATE",
  SEARCH: "SEARCH",
};

const INVITE_TYPES = {
  BASIC: "Basic Account Creation",
  WITH_LOAN_APP: "Invite & Start Loan Application",
};

const InviteBorrowerSidebar = ({ onClose }) => {
  const [step, setStep] = useState(STEP_TYPES.SEARCH);
  const [inviteType, setInviteType] = useState(INVITE_TYPES.BASIC);
  const [searchEmail, setSearchEmail] = useState("");
  const [foundUser, setFoundUser] = useState(null);

  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const {
    loading,
    searchBorrowerByEmail,
    resendBorrowerProfileInvite,
    createBorrowerProfileStaging,
    startStagedApplicationAndInvite,
  } = useBorrowerPortalHook();

  const handleClose = () => {
    setStep(STEP_TYPES.SEARCH);
    setSearchEmail("");
    setFoundUser(null);
    onClose?.();
  };

  const defaultWidth = useMemo(() => {
    if (window.innerWidth < 1400) return 540;
    return 780;
  }, []);

  const handleSearchEmail = async () => {
    const targetEmail = isArray(searchEmail) ? searchEmail[0] : searchEmail;
    if (!targetEmail?.trim()) return;

    searchBorrowerByEmail({
      email: targetEmail,
      onSuccessFn: (res) => {
        const foundLiveUserData = res?.user?.live_user;
        const foundStagedUserData = res?.user?.staged_user;

        if (!isEmpty(foundLiveUserData)) {
          setFoundUser(foundLiveUserData);
          setStep(STEP_TYPES.FOUND_LIVE);
          return;
        }

        if (!isEmpty(foundStagedUserData)) {
          setFoundUser(foundStagedUserData);
          setStep(STEP_TYPES.FOUND_STAGED);
          return;
        }

        setFoundUser(null);
        setStep(STEP_TYPES.CREATE);
      },
    });
  };

  const onCreateSubmit = (payload) => {
    if (inviteType === INVITE_TYPES.WITH_LOAN_APP) {
      // Extract borrower info and loan application data separately
      const { borrower, ...loanAppFields } = payload;
      const emailAddress = borrower?.emailAddress;

      // Build the applicationData object from the loan form fields
      // loanAppFields contains subjectProperties, borrowers, and any other
      // fields from CreateLoanFormQuestions
      const applicationData = { ...loanAppFields };

      startStagedApplicationAndInvite({
        emailAddress,
        applicationData,
        sendInvite: true,
        data: {
          firstName: borrower?.firstName,
          lastName: borrower?.lastName,
          phone: borrower?.phone,
        },
        onSuccessFn: () => {
          handleClose();
        },
      });
    } else {
      // Basic invite — just stage the borrower profile
      const borrowerData = payload?.borrower;
      createBorrowerProfileStaging({
        data: borrowerData,
        onSuccessFn: () => {
          handleClose();
        },
      });
    }
  };

  return (
    <ResizeableSidebar
      open
      hideBackdrop
      onClose={onClose}
      titleIcon={<SendOutlined />}
      title="Create Borrower Account"
      defaultWidth={defaultWidth}
    >
      <Flx column gap={0} sx={{ p: 0.5 }}>
        {step !== STEP_TYPES.SEARCH && (
          <Button
            sx={{ justifyContent: "flex-start", width: "max-content" }}
            variant="text"
            color="default"
            startIcon={<WestOutlined className="thin" />}
            onClick={() => {
              setStep(STEP_TYPES.SEARCH);
              setSearchEmail("");
              setFoundUser(null);
            }}
          >
            Back to Search
          </Button>
        )}

        {/* Step 1: Email Search */}
        {step === STEP_TYPES.SEARCH && (
          <Flx column gap={0}>
            <Txt bold>Check For Existing Account</Txt>
            <Txt secondary>
              Search by email to see if this borrower already has a portal
              account.
            </Txt>
            <Flx gap={1} alignItems="flex-start" sx={{ mt: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="borrower@example.com"
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchEmail();
                }}
                autoFocus
              />
              <Button
                variant="contained"
                onClick={handleSearchEmail}
                disabled={!searchEmail?.trim() || loading}
                endIcon={<SearchOutlined />}
                loading={loading}
                sx={{ whiteSpace: "nowrap", minWidth: 110 }}
              >
                {loading ? "Searching" : "Search"}
              </Button>
            </Flx>
          </Flx>
        )}

        {/* Step 2a: User Found (Live or Staged) */}
        {(step === STEP_TYPES.FOUND_STAGED || step === STEP_TYPES.FOUND_LIVE) &&
          foundUser && (
            <Flx column gap={2}>
              <Alert color="error" icon={<InfoOutlined />}>
                {step === STEP_TYPES.FOUND_STAGED ? (
                  <>
                    This borrower has a <b>pending invitation</b> — their
                    account was created but setup hasn't been completed.
                  </>
                ) : (
                  "This borrower already has an active portal account."
                )}
              </Alert>
              <Box
                sx={{
                  border: `1px solid ${grey[200]}`,
                  borderRadius: "8px",
                  p: 2.5,
                  bgcolor: "#fff",
                }}
              >
                <Flx column gap={3}>
                  <Flx fw jb ac>
                    <Flx column>
                      <Htag>
                        {foundUser?.fullName ||
                          `${foundUser?.firstName || ""} ${foundUser?.lastName || ""}`.trim()}
                      </Htag>
                      <Txt secondary>
                        {`${foundUser?.emailAddress} | ${foundUser?.phone ? formatPhone(foundUser?.phone) : "No Phone"}`}
                      </Txt>
                    </Flx>

                    {step === STEP_TYPES.FOUND_STAGED ? (
                      <Button
                        variant="contained"
                        size="medium"
                        disabled={TEMP_DISABLED}
                        loading={loading}
                        endIcon={
                          <SendOutlined sx={{ fontSize: "16px !important" }} />
                        }
                        onClick={() => {
                          resendBorrowerProfileInvite({
                            emailAddress: foundUser?.emailAddress,
                            onSuccessFn: () => {
                              // Optionally close or show confirmation
                            },
                          });
                        }}
                      >
                        Resend Invitation
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="medium"
                        endIcon={
                          <OpenInNewOutlined
                            sx={{ fontSize: "16px !important" }}
                          />
                        }
                        onClick={() => {
                          handleClose();
                          navigate(`/users/${foundUser._id}`);
                        }}
                      >
                        View User
                      </Button>
                    )}
                  </Flx>

                  <FlxGrid g={4} width="180px">
                    <DetailField
                      label="Created Date"
                      value={formatDate(foundUser.createdDate)}
                    />
                    <DetailField
                      label="Sign Up Date"
                      value={formatDateTime(foundUser.signUpDate)}
                    />
                    <DetailField
                      label="Last Login"
                      value={formatDate(foundUser.lastAccessed)}
                    />
                    <DetailField
                      label="Last Updated"
                      value={formatDateTime(foundUser.updateDateTime)}
                    />
                    <DetailField label="Email" value={foundUser.emailAddress} />
                    <DetailField
                      label="Phone"
                      value={formatPhone(foundUser.phone)}
                    />
                    <DetailField
                      label="Date of Birth"
                      value={
                        foundUser?.dateOfBirth
                          ? formatDate(foundUser.dateOfBirth)
                          : null
                      }
                    />
                    <DetailField
                      label="Profile Complete"
                      value={foundUser.profileComplete ? "Yes" : "No"}
                    />
                  </FlxGrid>
                </Flx>
              </Box>
            </Flx>
          )}

        {/* Step 2b: User Not Found — Create Account */}
        {step === STEP_TYPES.CREATE && (
          <Flx column gap={2}>
            <Alert
              icon={<CheckOutlined />}
              color="success"
              sx={{ border: `1px solid ${green[200]}` }}
            >
              No account found for <strong>{searchEmail}</strong>. Fill out the
              form below to create a new borrower account.
            </Alert>

            <BorrowerCreateAccount
              onSubmit={onCreateSubmit}
              initialEmail={searchEmail}
              user={user}
              inviteType={inviteType}
              setInviteType={setInviteType}
              loading={loading}
            />
          </Flx>
        )}
      </Flx>
    </ResizeableSidebar>
  );
};

const SwitchItem = ({ icon, title, description, onClick, active = false }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 2,
        flexGrow: 1,
        borderRadius: 2,
        flexBasis: "300px",
        flexShrink: 0,
        border: "2px solid",
        borderColor: active ? "primary.main" : "divider",
        bgcolor: active ? "primary.50" : "background.paper",
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: active ? "primary.main" : "action.disabled",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "action.hover",
          color: active ? "primary.contrastText" : "text.secondary",
          flexShrink: 0,
          transition: "all 0.2s ease",
          svg: {
            fontSize: 22,
            color: active ? "primary.main" : "text.secondary",
          },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.85rem",
            lineHeight: 1.3,
            color: active ? "text.primary" : "text.secondary",
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontSize: "0.7rem",
              lineHeight: 1.4,
              color: "text.secondary",
              mt: 0.25,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const BorrowerCreateAccount = ({
  onSubmit,
  initialEmail,
  user,
  loading,
  inviteType,
  setInviteType,
}) => {
  const showLoanApp = inviteType === INVITE_TYPES.WITH_LOAN_APP;

  const handleSubmit = (ffValues) => {
    // Pass the full form values up — the parent (onCreateSubmit) handles
    // splitting borrower data vs loan app data based on inviteType
    onSubmit(ffValues);
  };

  // Always include loan app structure so switching invite type doesn't
  // blow away form state via an initialValues change
  const initialValues = useMemo(() => {
    return {
      borrower: {
        emailAddress: initialEmail || "",
      },
      subjectProperties: [{}],
      borrowers: [
        {
          emailAddress: initialEmail || "",
        },
      ],
    };
  }, [initialEmail]);

  const submitText = showLoanApp
    ? "Create Account With Application"
    : "Create Account";

  return (
    <RffForm
      sx={{ my: 3 }}
      onSubmit={handleSubmit}
      loading={loading}
      disabled={TEMP_DISABLED}
      initialValues={initialValues}
      submitText={submitText}
    >
      {showLoanApp && <BorrowerFieldSync />}
      <Flx column gap={2}>
        <Flx g={0.5} sx={{ mb: 6 }} column>
          <Htag variant="h3">Creation Type</Htag>
          <Flx ac g={2} wrap>
            <SwitchItem
              active={inviteType === INVITE_TYPES.BASIC}
              icon={<PersonAddOutlined />}
              title="Basic Account"
              description="Create a basic account without starting a loan application."
              onClick={() => setInviteType(INVITE_TYPES.BASIC)}
            />
            <SwitchItem
              active={inviteType === INVITE_TYPES.WITH_LOAN_APP}
              icon={<DescriptionOutlined />}
              title="Create Account & Start Loan App"
              // title="Create Account & Start Loan Application"
              description="Create the account and kick off a loan application to get started faster."
              onClick={() => setInviteType(INVITE_TYPES.WITH_LOAN_APP)}
            />
          </Flx>
        </Flx>
        <Flx column gap={0}>
          <Flx
            column
            gap={1}
            sx={{
              // mb: "64px",
              // mb: 8,
              mb: 6,
              // mb: 5,

              //
            }}
          >
            <Htag variant="h3">Borrower Information</Htag>
            <Box
              sx={{
                border: "1px solid #eeeeee",
                p: 2.5,

                borderRadius: 2,
              }}
            >
              <RffGroup suppressBottomMargin>
                <RffTextField
                  required
                  name="borrower.firstName"
                  label="First Name"
                  size={6}
                />
                <RffTextField
                  required
                  name="borrower.lastName"
                  label="Last Name"
                  size={6}
                />
                <RffEmailField
                  required
                  disabled
                  name="borrower.emailAddress"
                  label="Email Address"
                />
                <RffPhoneField
                  required
                  name="borrower.phone"
                  label="Phone Number"
                />
              </RffGroup>
            </Box>
          </Flx>

          {showLoanApp && (
            <Flx column gap={1} sx={{ mb: 0 }}>
              <Htag variant="h3">Loan Application Questions</Htag>
              <CreateLoanFormQuestions
                mobile
                cardHeaderFontSize="14px"
                cardHeaderIconFontSize="15px"
                defaultExpanded={false}
                hideConfigSection
                hideFileUploadSection
                sx={{
                  mb: 5,
                  ".MuiPaper-root": {
                    border: "1px solid #eeeeee",
                    boxShadow: "none",
                  },
                }}
              />
            </Flx>
          )}
        </Flx>
      </Flx>
    </RffForm>
  );
};

const ee = {
  _id: "695fd0fc09b4b2c07a6cf671",
  applications: [],
  appraisalOrders: [],
  associatedEntities: [],
  backgroundReports: [],
  borrowerComments: [],
  borrowerDocuments: [],
  create_user_session_minutes: 720,
  createDateTime: "2026-01-07T16:15:57.189000",
  createdDate: "2026-01-08T15:45:01.361000",
  creditAuths: [],
  creditPulls: [],
  dateOfBirth: "2002-08-05T04:00:00.000Z",
  dealIds: [],
  email_domain: "lendmarq.com",
  email_lower: "holden.hargrave@lendmarq.com",
  emailAddress: "holden.hargrave@lendmarq.com",
  ff_retained_as_rental: {
    greater_than_three_years: 0,
    less_than_three_years: 0,
  },
  ff_sold: { greater_than_three_years: 0, less_than_three_years: 0 },
  firstName: "Holden",
  firstSignInDate: "2026-03-20T15:17:40.590000",
  fullName: "Holden Hargrave",
  general_contractor_projects: {
    greater_than_three_years: 0,
    less_than_three_years: 0,
  },
  guc_retained_as_rental: {
    greater_than_three_years: 0,
    less_than_three_years: 0,
  },
  guc_sold: { greater_than_three_years: 0, less_than_three_years: 0 },
  income_producing_properties: {
    greater_than_three_years: 0,
    less_than_three_years: 0,
  },
  invite_sent: false,
  lastAccessed: "2026-03-20T17:06:40.387000",
  lastName: "Hargrave",
  lastSignInDate: "2026-03-20T17:06:40.378000",
  loanQuotes: [],
  parsedDocuments: [],
  parsedId: {},
  permittedUsers: [],
  phone: "8625969045",
  preapprovals: [],
  primaryAddress: {
    city: "Coral Gables",
    coordinateTuple: [25.743761, -80.262763],
    country: "United States",
    countryCode: "US",
    county: "FL",
    fullAddress: "405 Malaga Avenue, Coral Gables, FL 33134",
    id: "Axy7IExbsM5xrsDUs_HeyQ-1767887177922",
    latitude: 25.743761,
    longitude: -80.262763,
    score: 1,
    state: "Florida",
    streetName: "Malaga Avenue",
    streetNumber: "405",
    zip: "33134",
  },
  profileComplete: true,
  propertyBids: [],
  referralPartner: "Handsome Homebuyer",
  referralSource: "Handsome Homebuyer",
  role: "Borrower",
  session_duration_minutes: 4320,
  signUpDate: "2026-01-07T16:15:57.189000",
  smsOptIn: false,
  staged_user: false,
  stagedApplications: [],
  stytch_user_id: "user-live-406dbc9b-aef6-445b-806c-4b9e6330afd6",
  subjectProperties: [],
  trusted_metadata: {
    emailAddress: "holden.hargrave@lendmarq.com",
    firstName: "Holden",
    lastName: "Hargrave",
    userId: "695fd0fc09b4b2c07a6cf671",
  },
  updatedBy: "holden.hargrave@lendmarq.com",
  updatedDate: "2026-01-08T15:46:29.112000",
  user_existed: true,
  user_project_type: "borrower_portal",
  validatedExperience: [],
};
const e = {
  _id: "69bdaf01c8b1d89a88f8cb99",
  activeStatus: "inactive",
  convertedDate: null,
  createdBy: "dev2@arrowservicing.com",
  createdDate: "2026-03-20T20:33:05.641000",
  email_domain: "gmail.com",
  emailAddress: "jolivacliffcomortgage@gmail.com",
  firstName: "JamesCC",
  firstSignInDate: null,
  hasSignedIn: false,
  invite_sent: false,
  invite_sent_date: null,
  invitedBy: null,
  inviteSent: false,
  isStaged: true,
  lastName: "OlivaCC",
  lastSignInDate: null,
  permittedUsers: ["dev2@arrowservicing.com"],
  phone: "6314563372",
  staged_user: true,
  stagedApplicationCount: 0,
  stagedApplications: [],
  stagedDate: "2026-03-20T20:33:05.641000",
  staging_user: {
    _id: "69209d06e28939fa8dfffaa0",
    company: "Lendmarq",
    createdDate: "2025-11-21T12:10:30.734000",
    emailAddress: "dev2@arrowservicing.com",
    emailSignature: " ",
    firstName: "Jimmy",
    fullName: "Jimmy Oliva",
    lastName: "Oliva",
    member_id: "member-live-1b0b5c95-55b7-47d5-82e7-385a6b3bfb92",
    organization_id: "organization-live-e345fd6c-502a-44a5-ab4c-cf836d816e10",
    permissions: ["admin"],
    phone: "6314563373",
    phone_number: null,

    roles: ["admin"],

    teams: [],
    testData: { testNumber: 3 },
    trusted_metadata: {
      emailAddress: "dev2@arrowservicing.com",
      firstName: "Jimmy",
      lastName: "Oliva",
    },
    user_project_type: "b2b",
  },
  stagingStatus: "staged",
  trusted_metadata: {
    emailAddress: "jolivacliffcomortgage@gmail.com",
    firstName: "JamesCC",
    lastName: "OlivaCC",
  },
  user_existed: false,
};
/**
 * Syncs borrower.* fields into borrowers[0].* so the loan application
 * section automatically picks up the name/email/phone entered above.
 * Must be rendered inside an RffForm.
 */
const BorrowerFieldSync = () => {
  const form = useForm();
  const { values } = useFormState({
    subscription: { values: true },
  });

  const borrower = values?.borrower;
  const prevRef = useRef({});

  useEffect(() => {
    if (!borrower) return;

    const fieldsToSync = {
      firstName: borrower.firstName,
      lastName: borrower.lastName,
      emailAddress: borrower.emailAddress,
      phone: borrower.phone,
    };

    // Only call form.change for fields that actually changed to avoid loops
    Object.entries(fieldsToSync).forEach(([key, value]) => {
      if (prevRef.current[key] !== value) {
        form.change(`borrowers[0].${key}`, value);
      }
    });

    prevRef.current = fieldsToSync;
  }, [
    borrower?.firstName,
    borrower?.lastName,
    borrower?.emailAddress,
    borrower?.phone,
    form,
  ]);

  return null;
};
export default InviteBorrowerSidebar;
